/**
 * OFFLINE QUEUE MANAGER
 * 
 * Queues mutations when offline and syncs when online
 * 
 * Features:
 * - Automatic queuing of failed mutations
 * - Auto-sync when connection restored
 * - Conflict resolution
 * - Persistent queue (survives browser refresh)
 * - Retry with exponential backoff
 */

import { get, set } from 'idb-keyval';
import { networkStatus } from './networkStatus';
import { toast } from 'sonner';

const OFFLINE_QUEUE_KEY = 'adjudon-offline-queue';
const MAX_RETRY_ATTEMPTS = 3;

export interface QueuedMutation {
  id: string;
  endpoint: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  data?: any;
  timestamp: number;
  retryCount: number;
  priority: number; // Higher = more important
}

class OfflineQueueManager {
  private queue: QueuedMutation[] = [];
  private isSyncing = false;
  private listeners: Set<() => void> = new Set();
  // Resolved once the persisted queue has been loaded from IndexedDB.
  // All write operations (add/sync/clear) await this before touching this.queue
  // to prevent overwriting in-flight mutations with stale IDB data.
  private readonly initPromise: Promise<void>;

  constructor() {
    this.initPromise = this.initialize();
  }

  private async initialize(): Promise<void> {
    await this.loadQueue();
    // Subscribe after the queue is populated so the length check is accurate.
    networkStatus.subscribe((isOnline) => {
      if (isOnline && this.queue.length > 0) {
        console.log('[Offline Queue] 🌐 Connection restored - syncing queue...');
        this.sync();
      }
    });
  }

  /**
   * Load queue from IndexedDB
   */
  private async loadQueue(): Promise<void> {
    try {
      const savedQueue = await get(OFFLINE_QUEUE_KEY);
      if (savedQueue && Array.isArray(savedQueue)) {
        this.queue = savedQueue;
        console.log(`[Offline Queue] ✓ Loaded ${this.queue.length} queued mutations`);
      }
    } catch (error) {
      console.error('[Offline Queue] Error loading queue:', error);
    }
  }

  /**
   * Save queue to IndexedDB
   */
  private async saveQueue(): Promise<void> {
    try {
      await set(OFFLINE_QUEUE_KEY, this.queue);
      this.notifyListeners();
    } catch (error) {
      console.error('[Offline Queue] Error saving queue:', error);
    }
  }

  /**
   * Add mutation to queue
   */
  async add(mutation: Omit<QueuedMutation, 'id' | 'timestamp' | 'retryCount'>): Promise<void> {
    await this.initPromise;
    const queuedMutation: QueuedMutation = {
      ...mutation,
      id: `${Date.now()}-${Math.random()}`,
      timestamp: Date.now(),
      retryCount: 0,
    };

    this.queue.push(queuedMutation);
    
    // Sort by priority (descending)
    this.queue.sort((a, b) => b.priority - a.priority);
    
    await this.saveQueue();
    
    console.log('[Offline Queue] ➕ Added mutation to queue:', queuedMutation);
    
    toast.info('Action saved. Will sync when online.', {
      description: `${mutation.method} ${mutation.endpoint}`,
    });

    // Try to sync immediately if online
    if (networkStatus.isOnline) {
      this.sync();
    }
  }

  /**
   * Remove mutation from queue
   */
  private async remove(id: string): Promise<void> {
    this.queue = this.queue.filter(m => m.id !== id);
    await this.saveQueue();
  }

  /**
   * Sync all queued mutations
   */
  async sync(): Promise<void> {
    await this.initPromise;
    if (this.isSyncing || this.queue.length === 0 || !networkStatus.isOnline) {
      return;
    }

    this.isSyncing = true;
    console.log(`[Offline Queue] 🔄 Syncing ${this.queue.length} mutations...`);

    const results = {
      success: 0,
      failed: 0,
    };

    // Process queue in order
    for (const mutation of [...this.queue]) {
      try {
        await this.executeMutation(mutation);
        await this.remove(mutation.id);
        results.success++;
      } catch (error) {
        console.error('[Offline Queue] Failed to sync mutation:', mutation, error);
        
        // Increment retry count
        mutation.retryCount++;
        
        if (mutation.retryCount >= MAX_RETRY_ATTEMPTS) {
          console.error('[Offline Queue] ❌ Max retries reached, removing from queue:', mutation);
          await this.remove(mutation.id);
          results.failed++;
        } else {
          console.log(`[Offline Queue] ⚠️ Retry ${mutation.retryCount}/${MAX_RETRY_ATTEMPTS}:`, mutation);
          await this.saveQueue();
        }
      }
    }

    this.isSyncing = false;

    if (results.success > 0) {
      toast.success(`Synced ${results.success} offline actions`, {
        description: results.failed > 0 ? `${results.failed} failed` : undefined,
      });
    }

    if (results.failed > 0) {
      toast.error(`Failed to sync ${results.failed} actions`, {
        description: 'Some actions could not be completed',
      });
    }

    console.log('[Offline Queue] ✓ Sync complete:', results);
  }

  /**
   * Execute a queued mutation
   */
  private async executeMutation(mutation: QueuedMutation): Promise<void> {
    const { endpoint, method, data } = mutation;

    // Import API client dynamically to avoid circular dependency
    const { default: apiClient } = await import('./apiClient');

    switch (method) {
      case 'POST':
        await apiClient.post(endpoint, data);
        break;
      case 'PUT':
        await apiClient.put(endpoint, data);
        break;
      case 'PATCH':
        await apiClient.patch(endpoint, data);
        break;
      case 'DELETE':
        await apiClient.delete(endpoint);
        break;
    }
  }

  /**
   * Get queue status
   */
  getStatus() {
    return {
      queueSize: this.queue.length,
      isSyncing: this.isSyncing,
      mutations: this.queue.map(m => ({
        id: m.id,
        endpoint: m.endpoint,
        method: m.method,
        timestamp: m.timestamp,
        retryCount: m.retryCount,
      })),
    };
  }

  /**
   * Clear all queued mutations
   */
  async clear(): Promise<void> {
    await this.initPromise;
    this.queue = [];
    await this.saveQueue();
    console.log('[Offline Queue] 🧹 Queue cleared');
  }

  /**
   * Subscribe to queue changes
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener());
  }
}

// Export singleton
export const offlineQueue = new OfflineQueueManager();
